// src/config.ts
// WHERE THE BACKEND IS, AND HOW THAT IS DECIDED. One place, no scattered string literals.
//
// Resolution order, first hit wins:
//   1. `?api=` in the URL          — for a tester pointing one tab at a branch deploy. Persisted.
//   2. localStorage `dcs.api_base` — whatever was chosen last.
//   3. `VITE_API_BASE` at build    — how a real deployment is pinned.
//   4. '' (same origin)            — which is what `npm run dev` uses, because vite proxies /v1
//                                     and /health to the real backend. See vite.config.ts and the
//                                     CORS note in the README: the gateway's allowlist fails CLOSED,
//                                     so a browser calling it cross-origin from localhost is refused
//                                     until someone adds that origin to ALLOWED_ORIGINS in Railway.
//                                     A dev proxy makes the call same-origin and sidesteps that
//                                     entirely; a DEPLOYED build cannot, and must be allowlisted.

const KEY = 'dcs.api_base';

function fromQuery(): string | null {
  if (typeof location === 'undefined') return null;
  try {
    const v = new URL(location.href).searchParams.get('api');
    return v && v.trim() ? v.trim().replace(/\/+$/, '') : null;
  } catch {
    return null;
  }
}

function fromStorage(): string | null {
  try {
    const v = localStorage.getItem(KEY);
    return v && v.trim() ? v.replace(/\/+$/, '') : null;
  } catch {
    return null;
  }
}

/** Build-time default. `import.meta.env` is absent under plain node (tests), hence the guard. */
function fromBuild(): string | null {
  try {
    const v = (import.meta as unknown as { env?: Record<string, string> }).env?.['VITE_API_BASE'];
    return v && v.trim() ? v.trim().replace(/\/+$/, '') : null;
  } catch {
    return null;
  }
}

let override: string | null = null;

/** Point the client at a different backend for the rest of this session (and remember it). */
export function setApiBase(base: string): void {
  override = base.trim().replace(/\/+$/, '');
  try { localStorage.setItem(KEY, override); } catch { /* private mode; session-only is fine */ }
}

export function apiBase(): string {
  if (override !== null) return override;
  const q = fromQuery();
  if (q !== null) { setApiBase(q); return q; }
  return fromStorage() ?? fromBuild() ?? '';
}

/** Shown on the Settings-less "where am I pointed" line, so a tester is never guessing. */
export function apiBaseLabel(): string {
  const b = apiBase();
  return b === '' ? `${typeof location === 'undefined' ? 'same origin' : location.origin} (dev proxy)` : b;
}

/**
 * THE NAMESPACE. Every route this client calls lives under it, and the kill switch guards exactly
 * this prefix. Named once so a screen cannot drift from the middleware's idea of the boundary.
 */
export const PRACTICE_NAMESPACE = '/v1/practice';
